import React, { useState, useEffect, useCallback, useRef } from "react";
import cn from "classnames";
import styles from "./Participants.module.sass";
import Card from "../../../components/Card";
import Form from "../../../components/Form";
import Table from "./Table";
import Panel from "./Panel";
import Icon from "../../../components/Icon";

// Services
import { getAttendancesBySessionId, deleteAttendance, subscribeToSessionAttendances, unsubscribeFromAttendances } from "../../../services/attendances.service";
import { sendSignatureInvitations } from "../../../services/signature.service";
import { showSuccess, showError } from "../../../services/toast.service";

const SignaturesAndAbsences = ({ session }) => {
    const [search, setSearch] = useState("");
    const [attendances, setAttendances] = useState([]);
    const [filteredAttendances, setFilteredAttendances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAttendances, setSelectedAttendances] = useState([]);
    const [sendingInvitations, setSendingInvitations] = useState(false);
    const isMountedRef = useRef(true);

    // Fonction de fetch réutilisable avec gestion du montage
    const fetchAttendances = useCallback(async () => {
        if (!session || !session.id) return;
        try {
            setLoading(true);
            const attendancesData = await getAttendancesBySessionId(session.id);
            // Vérifier si le composant est toujours monté
            if (isMountedRef.current) {
                setAttendances(attendancesData);
                setFilteredAttendances(attendancesData);
            }
        } catch (error) {
            // Ignorer les erreurs d'annulation automatique de PocketBase
            if (error?.status === 0 || error?.message?.includes('autocancelled')) {
                return;
            }
            console.error('Erreur lors de la récupération des attendances:', error);
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [session?.id]);

    useEffect(() => {
        isMountedRef.current = true;
        
        if (session) {
            fetchAttendances();
            
            // S'abonner aux changements en temps réel
            subscribeToSessionAttendances(session.id, (event) => {
                if (!isMountedRef.current) return;
                
                console.log('📡 Mise à jour temps réel:', event.action, event.record?.id);
                
                if (event.action === 'update') {
                    // Mettre à jour l'attendance modifiée
                    setAttendances(prev => prev.map(att => 
                        att.id === event.record.id ? event.record : att
                    ));
                    setFilteredAttendances(prev => prev.map(att => 
                        att.id === event.record.id ? event.record : att
                    ));
                } else if (event.action === 'create') {
                    // Ajouter la nouvelle attendance
                    setAttendances(prev => [...prev, event.record]);
                    setFilteredAttendances(prev => [...prev, event.record]);
                } else if (event.action === 'delete') {
                    // Supprimer l'attendance
                    setAttendances(prev => prev.filter(att => att.id !== event.record.id));
                    setFilteredAttendances(prev => prev.filter(att => att.id !== event.record.id));
                }
            });
        }
        
        return () => {
            isMountedRef.current = false;
            unsubscribeFromAttendances();
        };
    }, [session?.id, fetchAttendances]);

    const handleSelectAttendance = (attendanceId, isSelected) => {
        if (isSelected) {
            setSelectedAttendances([...selectedAttendances, attendanceId]);
        } else {
            setSelectedAttendances(selectedAttendances.filter(id => id !== attendanceId));
        }
    };

    const handleSelectAll = (isSelected) => {
        if (isSelected) {
            const allAttendanceIds = filteredAttendances.map(attendance => attendance.id);
            setSelectedAttendances([...allAttendanceIds]);
        } else {
            setSelectedAttendances([]);
        }
    };

    const handleClearSelection = () => {
        setSelectedAttendances([]);
    };

    const handleEditAttendance = (attendance) => {
        // Pour l'instant, on peut juste afficher les détails ou rediriger vers la page étudiant
        console.log('Éditer attendance:', attendance);
        // TODO: Implémenter la redirection vers la page de détail de l'étudiant
    };

    const handleDeleteAttendance = async (attendanceId) => {
        try {
            await deleteAttendance(attendanceId);
            
            // Mettre à jour l'état local
            const remainingAttendances = attendances.filter(a => a.id !== attendanceId);
            setAttendances(remainingAttendances);
            setFilteredAttendances(remainingAttendances.filter(attendance => {
                if (!search.trim()) return true;
                const searchLower = search.toLowerCase();
                const participant = attendance.expand?.participant_id;
                if (!participant) return false;
                const fullName = `${participant.first_name} ${participant.last_name}`.toLowerCase();
                const email = (participant.email || '').toLowerCase();
                return fullName.includes(searchLower) || email.includes(searchLower);
            }));
            
            showSuccess('Attendance supprimée avec succès.');
        } catch (error) {
            showError('Erreur lors de la suppression de l\'attendance.');
            console.error(error);
        }
    };

    const handleRemoveSelected = async (selectedIds) => {
        if (!selectedIds.length) {
            return;
        }
        
        try {
            // Supprimer chaque attendance sélectionnée
            for (const attendanceId of selectedIds) {
                await deleteAttendance(attendanceId);
            }
            
            // Mettre à jour l'état local
            const remainingAttendances = attendances.filter(a => !selectedIds.includes(a.id));
            setAttendances(remainingAttendances);
            setFilteredAttendances(remainingAttendances.filter(attendance => {
                if (!search.trim()) return true;
                const searchLower = search.toLowerCase();
                const participant = attendance.expand?.participant_id;
                if (!participant) return false;
                const fullName = `${participant.first_name} ${participant.last_name}`.toLowerCase();
                const email = (participant.email || '').toLowerCase();
                return fullName.includes(searchLower) || email.includes(searchLower);
            }));
            
            // Réinitialiser la sélection
            setSelectedAttendances([]);
            
            showSuccess(`${selectedIds.length} attendance(s) supprimée(s) avec succès.`);
        } catch (error) {
            showError('Erreur lors de la suppression des attendances.');
            console.error(error);
        }
    };

    const handleSendSignatureInvitations = async (selectedIds) => {
        if (!selectedIds.length) {
            showError('Veuillez sélectionner au moins une attendance.');
            return;
        }

        try {
            setSendingInvitations(true);
            
            // Envoyer directement les attendanceIds sélectionnés
            const result = await sendSignatureInvitations(selectedIds);
            
            if (result.success) {
                showSuccess(`Invitations de signature envoyées à ${result.sentCount} participant(s).`);
                
                // Rafraîchir les attendances pour voir les mises à jour
                await fetchAttendances();
                
                // Réinitialiser la sélection
                setSelectedAttendances([]);
            } else {
                showError(result.error || 'Erreur lors de l\'envoi des invitations.');
            }
        } catch (error) {
            console.error('Erreur lors de l\'envoi des invitations:', error);
            showError(error.message || 'Erreur lors de l\'envoi des invitations de signature.');
        } finally {
            setSendingInvitations(false);
        }
    };

    const handleSendSignatureInvitation = async (attendanceIds) => {
        try {
            setSendingInvitations(true);
            
            const result = await sendSignatureInvitations(attendanceIds);
            
            if (result.success) {
                showSuccess(`Invitation de signature envoyée avec succès.`);
                
                // Rafraîchir les attendances pour voir les mises à jour
                await fetchAttendances();
            } else {
                showError(result.error || 'Erreur lors de l\'envoi de l\'invitation.');
            }
        } catch (error) {
            console.error('Erreur lors de l\'envoi de l\'invitation:', error);
            showError(error.message || 'Erreur lors de l\'envoi de l\'invitation de signature.');
        } finally {
            setSendingInvitations(false);
        }
    };


    // Fonction de recherche
    useEffect(() => {
        if (!search.trim()) {
            setFilteredAttendances(attendances);
        } else {
            const searchLower = search.toLowerCase();
            const filtered = attendances.filter(attendance => {
                const participant = attendance.expand?.participant_id;
                if (!participant) return false;
                const fullName = `${participant.first_name} ${participant.last_name}`.toLowerCase();
                const email = (participant.email || '').toLowerCase();
                return fullName.includes(searchLower) || email.includes(searchLower);
            });
            setFilteredAttendances(filtered);
        }
    }, [search, attendances]);

    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        // filterTutors(search);
    };

    return (
        <>
            <Card
                className={styles.card}
                classCardHead={styles.head}
                title="Signatures & Absences"
                classTitle={cn("title-blue", styles.title)}
                head={
                    <>
                      <Form
                        className={styles.form}
                        value={search}
                        setValue={setSearch}
                        onSubmit={() => handleSubmit()}
                        placeholder="Rechercher par nom, prénom, email"
                        type="text"
                        name="search"
                        icon="search"
                      />
                    </>
                  }
                >
                <div className={styles.wrapper}>
                    {loading ? (
                        <div className={styles.loading}>
                            <div className="spinner-large" />
                        </div>
                    ) : filteredAttendances.length > 0 ? (
                        <Table 
                            items={filteredAttendances} 
                            selectedAttendances={selectedAttendances}
                            onSelectAttendance={handleSelectAttendance}
                            onSelectAll={handleSelectAll}
                            onEdit={handleEditAttendance}
                            onDelete={handleDeleteAttendance}
                            onSendSignatureInvitation={handleSendSignatureInvitation}
                            onAttendanceUpdate={fetchAttendances}
                            session={session}
                        />
                    ) : attendances.length > 0 ? (
                        <div className={styles.empty}>Aucune attendance ne correspond à votre recherche</div>
                    ) : (
                        <div className={styles.empty}>Aucune attendance trouvée pour cette session.</div>
                    )}
                </div>
            </Card>
            <Panel 
                selectedAttendances={selectedAttendances} 
                onRemoveSelected={handleRemoveSelected} 
                onClearSelection={handleClearSelection}
                onSendSignatureInvitations={handleSendSignatureInvitations}
                sendingInvitations={sendingInvitations}
            />
        </>
    );
};

export default SignaturesAndAbsences;
